// The two-option selector (AppIconChipToggle / AppToggleButton, role="group") whose buttons read exactly these labels
function findToggle(...labels) {
	const want = labels.map(l => l.toLowerCase())
	return [...document.querySelectorAll('[role="group"]')].find(group => {
		const names = [...group.querySelectorAll('button')].map(b => b.textContent.trim().toLowerCase())
		return want.every(w => names.includes(w))
	}) || null
}

// The left group of toggles of the filter panel (the first ancestor with more than one selector)
function findTogglesRow() {
	let el = findToggle('Total price', 'Daily')
	while (el && el.querySelectorAll('[role="group"]').length < 2) el = el.parentElement
	return el
}

// A filter field: its cell in the filters grid, found by its exact label
function findField(...texts) {
	const want = texts.map(t => t.toLowerCase())
	const label = [...document.querySelectorAll('label')].find(l => want.includes(l.textContent.trim().toLowerCase()))
	return label?.closest('.MuiGrid-item') || label?.closest('.MuiFormControl-root') || null
}

// The CSS grid that lays out the filter fields
function findFieldsGrid() {
	let el = findField('Providers')
	while (el && getComputedStyle(el).display !== 'grid') el = el.parentElement
	return el
}

// A card (real view or demo) by the exact text of its AppCardTitle
function findCard(title) {
	const el = [...document.querySelectorAll('span.MuiTypography-root')].find(e => e.textContent.trim() === title)
	return el?.closest('.MuiPaper-root') || null
}

// The header iconbuttons + saved filters selector, next to the Virtual Tour button
function findShareActions() {
	let el = document.querySelector('[data-icon="floppy-disk"], .fa-floppy-disk')?.closest('button')
	while (el && !el.querySelector('.MuiAutocomplete-root')) el = el.parentElement
	return el
}

// The engine measures the target 250 ms after a smooth scroll starts, too early for a long scroll (filters at
// the top, table at the bottom): every target here jumps into view at once, so the spotlight lands on it.
function instant(el) {
	return el && {
		scrollIntoView: () => el.scrollIntoView({ block: 'center' }),
		getBoundingClientRect: () => el.getBoundingClientRect(),
	}
}

const ES = navigator.language?.startsWith('es')

const STEPS = [
	{
		type: 'intro',
		icon: 'chart-mixed',
		title: ES ? 'Bienvenido a Business Intelligence' : 'Welcome to Business Intelligence',
		description: ES
			? 'La página de BI es tu herramienta avanzada de comparación de precios. Configura filtros detallados, compara precios entre proveedores, oficinas y periodos de tiempo, y visualiza los resultados en tablas y gráficos. Guarda y comparte tus configuraciones de filtros mediante URL.'
			: 'The BI page is your advanced price comparison tool. Configure detailed filters, compare prices across providers, offices, and time periods, then visualize results in tables and charts. Save and share your filter configurations via URL.',
	},
	{
		type: 'field',
		findTarget: () => findShareActions(),
		icon: 'link',
		title: ES ? 'Compartir y Guardar Filtros' : 'Share & Save Filters',
		description: ES
			? 'Tres acciones para gestionar tus configuraciones de filtros. Copia un enlace compartible con todos los filtros actuales codificados en la URL, guarda la configuración con un nombre personalizado para más tarde, o carga un conjunto de filtros guardado previamente desde el desplegable.'
			: 'Three actions to manage your filter configurations. Copy a shareable link with all current filters encoded in the URL, save the configuration with a custom name for later, or load a previously saved filter set from the dropdown.',
		example: ES
			? 'Copiar Enlace | Guardar Filtros | Cargar Filtros Guardados'
			: 'Copy Link | Save Filters | Load Saved Filters',
	},
	{
		type: 'field',
		findTarget: () => findTogglesRow(),
		icon: 'toggle-on',
		title: ES ? 'Selectores de Filtro' : 'Filter Toggles',
		description: ES
			? 'Selectores rápidos para cambiar modos de filtrado: precio total o diario, Oficinas individuales o Grupos de Oficinas, Sitios individuales o Grupos de Sitios, y un solo periodo o la comparación con un periodo anterior.'
			: 'Quick toggles to change filter modes: total or daily price, individual Offices vs Office Groups, individual Sites vs Site Groups, and a single period or a comparison with a previous period.',
		example: '"Daily" | "Office group" | "Site group" | "Compare periods"',
	},
	{
		type: 'field',
		findTarget: () => findField('How to group'),
		icon: 'layer-group',
		title: ES ? 'Método de Agrupación' : 'Grouping Method',
		description: ES
			? 'Define cómo se organizan los resultados en la tabla. Elige cómo cruzar tus datos: por proveedor y fecha de recogida, categoría y recogida, LOR y recogida, fecha de reserva, o proveedor y oficina. Esto determina la estructura de la tabla.'
			: 'Defines how results are organized in the table. Choose how to cross-reference your data: by provider and pickup date, category and pickup, LOR and pickup, booking date, or provider and office. This determines the table structure.',
		example: '"Provider Pickup" | "Category Pickup" | "Lor Pickup" | "Office Booking"...',
	},
	{
		type: 'field',
		findTarget: () => findFieldsGrid(),
		icon: 'filter',
		title: ES ? 'Filtros de Búsqueda' : 'Search Filters',
		description: ES
			? 'Configura tu comparación de precios: selecciona oficinas, sitios, rangos de fechas de reserva y recogida, agrupación, categorías, LOR, niveles, proveedores y moneda. Cambia el selector de la derecha a «Show features» para filtros avanzados como ACRISS, kilometraje, transmisión, política de combustible y seguros.'
			: 'Configure your price comparison: select offices, sites, booking and pickup date ranges, grouping, categories, LOR, tiers, providers, and currency. Switch the toggle on the right to "Show features" for advanced filters like ACRISS, mileage, transmission, fuel policy, and insurance.',
	},
	{
		type: 'field',
		findTarget: () => findToggle('Min. Price', 'Avg. Price', 'Max. Price')?.parentElement || null,
		skipIf: () => !findToggle('Min. Price', 'Avg. Price', 'Max. Price'),
		icon: 'sliders',
		title: ES ? 'Acciones de la Tabla' : 'Table Actions',
		description: ES
			? 'Controles en la cabecera de la tabla de resultados. Alterna entre precios Mínimo, Medio y Máximo. Cuando se devuelven varias oficinas, aparece un filtro de oficina para centrarse en una ubicación específica, además de un botón para descargar todas las oficinas como Excel.'
			: 'Controls in the header of the results table. Toggle between Min, Average, and Max prices. When multiple offices are returned, an office filter appears to focus on one specific location, plus a button to download all offices as Excel.',
		example: ES
			? '«Min. Price» / «Avg. Price» / «Max. Price» | «Filter by specific office» | Descargar Excel de todas las oficinas'
			: '"Min. Price" / "Avg. Price" / "Max. Price" | "Filter by specific office" | Download all offices Excel',
	},
	{
		type: 'field',
		findTarget: () => findCard('Price Comparison'),
		skipIf: () => !findCard('Price Comparison'),
		icon: 'table-cells',
		title: ES ? 'Tabla de Comparación de Precios' : 'Price Comparison Table',
		description: ES
			? 'Tabla de resultados con la primera columna fija a la izquierda (categoría, proveedor u oficina según la agrupación). Las columnas de fecha muestran precios con bordes codificados por color: verde para el más barato, rojo para el más caro. Cada celda incluye el precio actual, el precio anterior y el porcentaje de evolución con flechas direccionales.'
			: 'Results table with the first column sticky on the left (category, provider, or office depending on grouping). Date columns show prices with color-coded borders: green for cheapest, red for most expensive. Each cell includes the current price, previous price, and evolution percentage with directional arrows.',
	},
	{
		type: 'field',
		findTarget: () => findCard('Chart'),
		skipIf: () => !findCard('Chart'),
		icon: 'chart-line',
		title: ES ? 'Gráfico de Tendencia de Precios' : 'Price Trend Chart',
		description: ES
			? 'Gráfico de líneas que visualiza las tendencias de precios a lo largo del tiempo. Cada serie representa un proveedor o categoría. Cuando se comparan periodos, las líneas discontinuas muestran el periodo previo. Haz clic en los elementos de la leyenda para mostrar/ocultar series individuales. El selector Mín./Media/Máx. también se aplica aquí.'
			: 'Line chart visualizing price trends over time. Each series represents a provider or category. When comparing periods, dashed lines show the prior period. Click legend items to show/hide individual series. Toggle Min/Avg/Max applies here too.',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Configura tus filtros y pulsa «Search». Usa el selector Mín./Media/Máx. para cambiar la vista de precios. Comparte tu análisis con el botón de copiar enlace, o guárdalo para más tarde. La tabla y el gráfico se actualizan juntos — explora tendencias y detecta ventajas competitivas.'
			: 'Configure your filters and hit "Search". Use the Min/Avg/Max toggle to switch price views. Share your analysis via the copy link button, or save it for later. The table and chart update together — explore trends and spot competitive advantages.',
	},
]

export const TOUR_STEPS = STEPS.map(step => step.findTarget ? { ...step, findTarget: () => instant(step.findTarget()) } : step)
